import { CircleCheck, Info, TriangleAlert } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts';

import MarkdownText from './MarkdownText';
import RecordCard from './RecordCard';

import { AGENT_SHOW_TYPES } from '@/app/agentConversation/_enums';
import { parseRef, toBarChartData } from '@/app/agentConversation/_helpers';
import { formatNumber, formatNumberToLabel } from '@/helpers/numberHelpers';
import { cn } from '@/helpers/shadcn';

import { Alert, AlertDescription } from '@/app/ui/alert';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/app/ui/chart';
import { Error } from '@/app/ui/error';
import { Loading } from '@/app/ui/loading';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/app/ui/table';
import { useCurrencies } from '@/data/currency';

/** Something the assistant showed below its text: records, a table, figures, a chart or a note. */
const ShowBlock = ({ block }) => {
  const Part = SHOW_PARTS[block.show];
  if (!Part) return null;

  return (
    <section className="my-3 space-y-2" data-testid={`agent-show-${block.show}`}>
      {block.title && <h4 className="text-sm font-semibold">{block.title}</h4>}
      <Part block={block} />
    </section>
  );
};

// Everything below is model output, so nothing is assumed unique: a list's
// place is its key.
/* eslint-disable react/no-array-index-key */

// A card's total is money in its own currency, read from the currency list:
// the panel sits above every route, so nothing has loaded that list for it.
const ShowCards = ({ block }) => {
  const currencies = useCurrencies();
  if (currencies.isLoading && !currencies.data) return <Loading />;
  if (currencies.error) return <Error message="An error occurred loading currencies" />;

  return (
    <div className="space-y-2">
      {(block.refs ?? [])
        .map(parseRef)
        .filter(Boolean)
        .map((refValue, index) => (
          <RecordCard key={index} refValue={refValue} />
        ))}
    </div>
  );
};

const ShowTable = ({ block }) => (
  <div className="rounded-md border">
    <Table className="text-xs">
      <TableHeader className="bg-muted/50">
        <TableRow>
          {(block.columns ?? []).map((column, index) => (
            <TableHead key={index} className="px-2 font-semibold">
              {column}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {(block.rows ?? []).map((row, rowIndex) => (
          <TableRow key={rowIndex}>
            {row.map((cell, cellIndex) => (
              <TableCell key={cellIndex} className={cn('px-2 align-top', typeof cell === 'number' && 'text-right tabular-nums')}>
                {typeof cell === 'number' ? formatNumber(cell) : <MarkdownText markdown={String(cell)} inline />}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
);

const ShowStats = ({ block }) => (
  <div className="grid grid-cols-2 gap-2">
    {(block.stats ?? []).map((stat, index) => (
      <div key={index} className="bg-card rounded-lg border p-3" data-testid="agent-stat">
        <div className="text-muted-foreground text-xs">{stat.label}</div>
        <div className="mt-0.5 text-lg font-semibold tabular-nums">{stat.value}</div>
        {stat.hint && <div className="text-muted-foreground mt-0.5 text-xs">{stat.hint}</div>}
      </div>
    ))}
  </div>
);

/* eslint-enable react/no-array-index-key */

const BAR_HEIGHT_PX = 32;
const CHART_MIN_HEIGHT_PX = 120;

const ShowBars = ({ block }) => {
  const bars = toBarChartData(block.bars);
  const config = { value: { label: block.unit || 'Value', color: 'var(--chart-1)' } };

  return (
    // The height follows the number of bars, so it is computed.
    <ChartContainer config={config} className="aspect-auto w-full" style={{ height: Math.max(CHART_MIN_HEIGHT_PX, bars.length * BAR_HEIGHT_PX + 24) }}>
      <BarChart data={bars} layout="vertical" margin={{ left: 0, right: 16 }}>
        <CartesianGrid horizontal={false} />
        <YAxis dataKey="label" type="category" width={110} tickLine={false} axisLine={false} tick={{ fontSize: 11 }} />
        <XAxis type="number" tickLine={false} axisLine={false} tickFormatter={formatNumberToLabel} tick={{ fontSize: 11 }} />
        <ChartTooltip content={<ChartTooltipContent hideLabel={false} />} />
        <Bar dataKey="value" fill="var(--color-value)" radius={3} />
      </BarChart>
    </ChartContainer>
  );
};

// A tone the panel does not know reads as info.
const CALLOUT_TONES = {
  info: { icon: Info, variant: 'default' },
  success: { icon: CircleCheck, variant: 'success' },
  warning: { icon: TriangleAlert, variant: 'warning' }
};

const ShowCallout = ({ block }) => {
  const tone = CALLOUT_TONES[block.tone] ?? CALLOUT_TONES.info;
  const Icon = tone.icon;

  return (
    <Alert variant={tone.variant}>
      <Icon />
      <AlertDescription>
        <MarkdownText markdown={block.text ?? ''} />
      </AlertDescription>
    </Alert>
  );
};

const SHOW_PARTS = {
  [AGENT_SHOW_TYPES.CARDS]: ShowCards,
  [AGENT_SHOW_TYPES.TABLE]: ShowTable,
  [AGENT_SHOW_TYPES.STATS]: ShowStats,
  [AGENT_SHOW_TYPES.BARS]: ShowBars,
  [AGENT_SHOW_TYPES.CALLOUT]: ShowCallout
};

export default ShowBlock;
